import { useCallback, useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const CLIENT_KEY = process.env.REACT_APP_MIDTRANS_CLIENT_KEY;
const STORE = "rm_last_order";
let snapPromise = null;

const loadSnap = () => {
  if (window.snap) return Promise.resolve(window.snap);
  if (snapPromise) return snapPromise;
  snapPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    const host = CLIENT_KEY?.startsWith("SB-") ? "app.sandbox.midtrans.com" : "app.midtrans.com";
    s.src = `https://${host}/snap/snap.js`;
    s.setAttribute("data-client-key", CLIENT_KEY || "");
    s.onload = () => resolve(window.snap);
    s.onerror = () => { snapPromise = null; reject(new Error("snap load failed")); };
    document.head.appendChild(s);
  });
  return snapPromise;
};

const refreshOrder = async (orderId) => {
  const r = await fetch(`${API}/api/orders/${orderId}/refresh`, { method: "POST" });
  if (!r.ok) throw new Error("refresh failed");
  return r.json();
};

export const paymentsEnabled = !!(API && CLIENT_KEY);

export const usePurchase = () => {
  const [state, setState] = useState({ open: false, phase: "idle", order: null, title: "" });

  const check = useCallback(async (orderId, title) => {
    setState((s) => ({ ...s, open: true, phase: "checking", title: title ?? s.title }));
    try {
      const order = await refreshOrder(orderId);
      const phase = order?.status === "paid" ? "paid" : order?.status === "failed" ? "failed" : "pending";
      localStorage.setItem(STORE, JSON.stringify({ id: orderId, title: order?.title, done: phase !== "pending" }));
      setState({ open: true, phase, order, title: order?.title ?? title });
    } catch {
      setState((s) => ({ ...s, phase: "error" }));
    }
  }, []);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(STORE) || "null");
    if (!saved?.id || saved.done || !API) return;
    refreshOrder(saved.id).then((o) => {
      if (o?.status === "paid" || o?.status === "failed") check(saved.id, saved.title);
    }).catch(() => {});
  }, [check]);

  const buy = useCallback(async (book) => {
    setState({ open: true, phase: "creating", order: null, title: book.title });
    try {
      const [snap, res] = await Promise.all([
        loadSnap(),
        fetch(`${API}/api/checkout`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ slug: book.slug }) }),
      ]);
      if (!res.ok) throw new Error("checkout failed");
      const { order_id, snap_token } = await res.json();
      localStorage.setItem(STORE, JSON.stringify({ id: order_id, title: book.title, done: false }));
      setState((s) => ({ ...s, open: false, phase: "paying", order: { order_id } }));
      snap.pay(snap_token, {
        onSuccess: () => check(order_id, book.title),
        onPending: () => check(order_id, book.title),
        onError: () => setState((s) => ({ ...s, open: true, phase: "failed" })),
        onClose: () => check(order_id, book.title),
      });
    } catch {
      setState((s) => ({ ...s, open: true, phase: "error" }));
    }
  }, [check]);

  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  return { ...state, buy, check, close };
};
